import {
  Alert,
  Badge,
  Button,
  createStyles,
  FileInput,
  Group,
  Paper,
  Radio,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  UnstyledButton
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconCategory,
  IconClipboard,
  IconInfoCircle,
  IconPaperclip
} from '@tabler/icons'
import { useRouter } from 'next/router'

import { useFetch } from '@/lib/hooks'
import { bytesToSize, getUrlString } from '@/lib/utils'

import { acceptTypes } from '../constants'

function normalizeCategoryKeywords(keywords: unknown): string[] {
  if (!Array.isArray(keywords)) return []
  return keywords
    .filter(k => k != null)
    .map(k => String(k).trim())
    .filter(Boolean)
}

interface Props {
  inputProps: any
  onSelect?: (category: any) => void
}

const useStyles = createStyles(theme => ({
  option: {
    ':hover': {
      backgroundColor: theme.colors.gray[2]
    }
  }
}))

export function CategoriesListField({ inputProps, onSelect }: Props) {
  // Hooks
  const router = useRouter()
  const { classes } = useStyles()

  // Constants
  const { site } = router.query || {}
  const siteSlug = getUrlString(site)
  const { handleResetForm, selected, sellerSlug } = inputProps || {}

  // Fetch
  const { data: categoriesData, isValidating } = useFetch([
    siteSlug ? `/${siteSlug}/support/categories/` : null,
    { seller: sellerSlug }
  ])

  if (selected) {
    const descriptionText =
      typeof selected?.description === 'string' ? selected.description.trim() : ''
    const keywordTags = normalizeCategoryKeywords(selected?.keywords)
    return (
      <Paper withBorder>
        <Stack spacing="sm" p="sm">
          <Text size="sm">Categoria selecionada:</Text>
          <Group spacing="sm" position="apart" align="flex-start" noWrap>
            <Group spacing="sm" align="flex-start" sx={{ flex: 1, minWidth: 0 }}>
              <Radio defaultChecked value="" mt={5} sx={{ flexShrink: 0 }} />
              <ThemeIcon sx={{ flexShrink: 0 }}>
                <IconCategory />
              </ThemeIcon>
              <Text sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{selected.name}</Text>
            </Group>
            <Button variant="outline" size="xs" onClick={handleResetForm} sx={{ flexShrink: 0 }}>
              Alterar
            </Button>
          </Group>
          {!!descriptionText && (
            <Alert icon={<IconInfoCircle />} color="blue" p="sm">
              <Text size="sm" color="dimmed">
                {descriptionText}
              </Text>
            </Alert>
          )}
          {keywordTags.length > 0 && (
            <Group spacing={8} noWrap={false} align="center">
              {keywordTags.map((label: string, index: number) => (
                <Badge
                  key={`${label}-${index}`}
                  variant="light"
                  color="blue"
                  size="sm"
                  radius="sm"
                  sx={theme => ({
                    border: 'none',
                    fontWeight: 600,
                    backgroundColor: theme.colors.blue[0],
                    color: theme.colors.blue[7],
                    padding: `4px ${theme.spacing.xs}px`,
                    height: 'auto',
                    lineHeight: 1.35
                  })}>
                  {label}
                </Badge>
              ))}
            </Group>
          )}
        </Stack>
      </Paper>
    )
  }

  return (
    <Paper withBorder>
      <Stack p="md" spacing="md">
        <Text size="sm">Selecione uma das categorias para abertura do atendimento:</Text>
        {!isValidating && categoriesData?.results && !categoriesData?.results?.length && (
          <Alert icon={<IconAlertTriangle />} color="orange">
            <Text>
              Não encontramos nenhuma categoria para a abertura do atendimento. Não será possível
              criar o atendimento no momento.
            </Text>
          </Alert>
        )}
      </Stack>
      <Stack spacing={0}>
        {categoriesData?.results?.map((category: any) => {
          const selectedCategoryUid = selected?.uid
          const { name, uid } = category || {}
          return (
            <UnstyledButton
              key={uid}
              p="sm"
              onClick={() => (selectedCategoryUid !== uid ? onSelect?.(category) : null)}
              className={classes.option}>
              <Group spacing="sm">
                <Radio value={uid} mt={3} defaultChecked={selectedCategoryUid === uid} />
                <ThemeIcon>
                  <IconCategory />
                </ThemeIcon>
                <Text>{name}</Text>
              </Group>
            </UnstyledButton>
          )
        })}
      </Stack>
    </Paper>
  )
}

export const SubjectField = ({ inputProps }: Props) => (
  <TextInput {...inputProps} name="title" type="text" placeholder="Assunto" />
)

export const DescriptionField = ({ inputProps }: Props) => (
  <Textarea
    {...inputProps}
    name="description"
    type="text"
    placeholder="Escreva com detalhes suas dúvidas e/ou problemas"
    multiple
    minRows={5}
    maxRows={5}
  />
)

export const AttachmentsField = ({ inputProps }: Props) => {
  const {
    disabled,
    hasAttachment,
    handleFileUpload,
    handleRemoveAttachment,
    handleClearAttachments,
    value,
    required = false,
    error
  } = inputProps || {}

  const isRequiredAndEmpty = required && !hasAttachment

  return (
    <>
      <Alert
        color={isRequiredAndEmpty ? 'red' : 'blue'}
        sx={{
          border: isRequiredAndEmpty ? '2px solid #ff6b35' : undefined,
          backgroundColor: isRequiredAndEmpty ? '#fff5f0' : undefined
        }}>
        <Text mb="sm" weight={isRequiredAndEmpty ? 500 : 400}>
          {required && (
            <Text component="span" color="red" weight={700} mr={4}>
              *
            </Text>
          )}
          Selecione os arquivos que deseja anexar ao chamado. (Máximo de <em>5MB</em> por arquivo)
          {required && (
            <Text component="span" color="red" weight={500} ml={4}>
              (Obrigatório)
            </Text>
          )}
        </Text>
        {!!error && (
          <Text size="xs" color="red" mb="sm" weight={500}>
            {error}
          </Text>
        )}
        <Button
          variant="outline"
          component="label"
          disabled={disabled}
          leftIcon={<IconPaperclip />}
          color={isRequiredAndEmpty ? 'red' : undefined}
          sx={{
            borderColor: isRequiredAndEmpty ? '#ff6b35' : undefined,
            '&:hover': {
              backgroundColor: isRequiredAndEmpty ? '#f3ecebe0' : undefined
            }
          }}>
          <Text weight={700}>{!hasAttachment ? 'Anexar arquivos' : 'Anexar mais arquivos'}</Text>
          <FileInput
            key={value?.length}
            multiple
            accept={acceptTypes.join(',')}
            onChange={handleFileUpload}
            sx={{ display: 'none' }}
          />
        </Button>
      </Alert>

      {hasAttachment && (
        <Paper p="sm" withBorder>
          <Group mb="sm" position="apart">
            <Text>Arquivos anexados:</Text>
            <Button size="xs" onClick={handleClearAttachments}>
              Limpar
            </Button>
          </Group>
          <Stack spacing="xs">
            {value?.map((file: any) => (
              <Group position="apart" key={file?.name} noWrap>
                <Group style={{ flex: 1, minWidth: 0 }}>
                  <IconClipboard />
                  <Text style={{ wordBreak: 'break-all', flex: 1 }}>
                    {file?.name} {bytesToSize(file?.size)}
                  </Text>
                </Group>
                <Button size="xs" variant="outline" onClick={() => handleRemoveAttachment(file)}>
                  REMOVER
                </Button>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}
    </>
  )
}
